import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSession } from '@/lib/store';
import { ShieldAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function AdminGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null);
  const isRTL = i18n.language === 'ar';

  useEffect(() => {
    const session = getSession();
    const role = session?.role?.toUpperCase();
    if (!session || (role !== 'ADMIN' && role !== 'SUPER_ADMIN' && role !== 'ADMINISTRATOR')) {
      setIsAuthorized(false);
      const timer = setTimeout(() => {
        navigate('/dashboard', { replace: true });
      }, 2500);
      return () => clearTimeout(timer);
    } else {
      setIsAuthorized(true);
    }
  }, [navigate]);

  if (isAuthorized === null) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-bg-primary text-text-muted">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-accent-primary/20 border-t-accent-primary rounded-full animate-spin"></div>
          <p className="text-[10px] font-bold tracking-widest uppercase animate-pulse">
            {isRTL ? 'التحقق من الصلاحيات...' : 'Vérification des privilèges...'}
          </p>
        </div>
      </div>
    );
  }

  if (isAuthorized === false) {
    return (
      <div className="flex h-screen w-full flex-col items-center justify-center bg-bg-primary text-text-primary px-6 select-none">
        <div className="w-16 h-16 rounded-2xl bg-error/10 border border-error/20 flex items-center justify-center text-error mb-6 animate-bounce shadow-lg shadow-error/10">
          <ShieldAlert size={32} />
        </div>
        <h2 className="text-xl font-black uppercase tracking-tight mb-2 font-outfit">
          {isRTL ? 'وصول غير مصرح به' : 'Accès Non Autorisé'}
        </h2>
        <p className="text-sm text-text-secondary text-center max-w-md font-medium">
          {isRTL 
            ? 'يتطلب هذا القسم امتيازات مشرف النظام. يتم الآن إعادة توجيهك...'
            : 'Ce module requiert des privilèges d\'administration. Redirection en cours...'}
        </p>
        <div className="w-32 h-1 bg-bg-secondary relative overflow-hidden rounded-full border border-border mt-6">
          <div className="absolute inset-y-0 h-full w-12 bg-error shadow-[0_0_10px_rgba(239,68,68,0.5)] animate-[shimmer_1.5s_infinite]" />
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
